import React, { useEffect, useMemo, useState } from 'react';
import { getPUC, crearCuenta, editarCuenta, borrarCuenta } from '../services/api';
import FiltroPanel from '../components/FiltroPanel';

export default function PlanCuentas() {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [mostrarInactivas, setMostrarInactivas] = useState(false);
  const [expandidas, setExpandidas] = useState(new Set());
  const [modal, setModal] = useState(null); // { tipo: 'crear'|'editar', cuenta? }

  const cargar = async () => {
    setLoading(true);
    try {
      const params = mostrarInactivas ? {} : { activa: true };
      const res = await getPUC(params);
      setCuentas(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar PUC');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [mostrarInactivas]);

  // Filtrado por búsqueda — si una cuenta coincide, también muestra sus padres
  const cuentasFiltradas = useMemo(() => {
    if (!busqueda.trim()) return cuentas;
    const q = busqueda.toLowerCase().trim();
    const matches = cuentas.filter(c =>
      c.codigo.toLowerCase().includes(q) || c.nombre.toLowerCase().includes(q)
    );
    const visibles = new Set(matches.map(c => c.codigo));
    matches.forEach(m => {
      let p = m.padre_codigo;
      while (p) {
        visibles.add(p);
        const padre = cuentas.find(x => x.codigo === p);
        p = padre?.padre_codigo;
      }
    });
    return cuentas.filter(c => visibles.has(c.codigo));
  }, [cuentas, busqueda]);

  // Agrupar por padre para render jerárquico
  const porPadre = useMemo(() => {
    const m = new Map();
    cuentasFiltradas.forEach(c => {
      const k = c.padre_codigo || '__root__';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(c);
    });
    return m;
  }, [cuentasFiltradas]);

  const toggle = (codigo) => {
    setExpandidas(prev => {
      const s = new Set(prev);
      s.has(codigo) ? s.delete(codigo) : s.add(codigo);
      return s;
    });
  };

  // Si hay búsqueda activa, expandir todo automáticamente
  const efectivamenteExpandida = (codigo) =>
    busqueda.trim() ? true : expandidas.has(codigo);

  const handleEliminar = async (codigo) => {
    if (!window.confirm(`¿Desactivar/eliminar la cuenta ${codigo}?`)) return;
    try {
      await borrarCuenta(codigo);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleActivar = async (codigo, activa) => {
    try {
      await editarCuenta(codigo, { activa });
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al actualizar');
    }
  };

  const renderRama = (padre_codigo, depth = 0) => {
    const hijos = porPadre.get(padre_codigo) || [];
    return hijos.map(c => {
      const tieneHijos = (porPadre.get(c.codigo) || []).length > 0;
      const abierta = efectivamenteExpandida(c.codigo);
      return (
        <React.Fragment key={c.codigo}>
          <tr style={{
            background: c.activa ? '#fff' : '#f8d7da',
            opacity: c.activa ? 1 : 0.6,
          }}>
            <td style={{ ...S.td, paddingLeft: 8 + depth * 18 }}>
              {tieneHijos ? (
                <button onClick={() => toggle(c.codigo)} style={S.btnExpand}>
                  {abierta ? '▼' : '▶'}
                </button>
              ) : <span style={{ display: 'inline-block', width: 18 }} />}
              <span style={{ fontFamily: 'monospace', fontWeight: c.nivel <= 3 ? 700 : 400 }}>
                {c.codigo}
              </span>
            </td>
            <td style={{ ...S.td, fontWeight: c.nivel <= 2 ? 700 : c.nivel === 3 ? 600 : 400 }}>
              {c.nombre}
            </td>
            <td style={S.td}>{c.naturaleza === 'D' ? 'Débito' : 'Crédito'}</td>
            <td style={S.td}>{c.acepta_movimiento ? '✓' : '—'}</td>
            <td style={S.td}>
              {c.es_personalizada
                ? <span style={S.badgeUser}>Propia</span>
                : <span style={S.badgeStd}>Decreto 2650</span>}
            </td>
            <td style={S.tdAcciones}>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                {c.acepta_movimiento && (
                  <button style={S.btnAdd}
                    onClick={() => setModal({ tipo: 'crear', padre: c })}>
                    + Auxiliar
                  </button>
                )}
                <button style={S.iconBtn} title="Editar"
                  onClick={() => setModal({ tipo: 'editar', cuenta: c })}>
                  ✏️
                </button>
                {c.activa
                  ? <button style={{ ...S.iconBtn, borderColor: '#7f1d1d' }} title="Desactivar" onClick={() => handleEliminar(c.codigo)}>🚫</button>
                  : <button style={S.btnReact} onClick={() => handleActivar(c.codigo, true)}>Reactivar</button>}
              </div>
            </td>
          </tr>
          {abierta && renderRama(c.codigo, depth + 1)}
        </React.Fragment>
      );
    });
  };

  return (
    <div style={S.container}>
      <div style={S.header}>
        <h1 style={S.title}>Plan Único de Cuentas</h1>
        <p style={S.sub}>Decreto 2650/1993 — base copiada al crear la empresa, editable</p>
      </div>

      <div style={S.toolbar}>
        <FiltroPanel
          fields={[{ tipo: 'input', label: 'Buscar', value: busqueda, onChange: setBusqueda, placeholder: 'Código o nombre…', width: 230 }]}
          onClear={() => setBusqueda('')}
        />
        <label style={S.checkbox}>
          <input
            type="checkbox"
            checked={mostrarInactivas}
            onChange={e => setMostrarInactivas(e.target.checked)}
          />
          Mostrar inactivas
        </label>
        <span style={S.count}>{cuentasFiltradas.length} cuentas</span>
      </div>

      {error && <div style={S.error}>{error}</div>}

      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Código</th>
            <th style={S.th}>Nombre</th>
            <th style={S.th}>Naturaleza</th>
            <th style={S.th}>Movimiento</th>
            <th style={S.th}>Origen</th>
            <th style={S.th}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading
            ? <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', padding: 40 }}>Cargando…</td></tr>
            : renderRama('__root__')}
        </tbody>
      </table>

      {modal && (
        <ModalCuenta
          tipo={modal.tipo}
          cuenta={modal.cuenta}
          padre={modal.padre}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); cargar(); }}
        />
      )}
    </div>
  );
}

function ModalCuenta({ tipo, cuenta, padre, onClose, onSaved }) {
  const editando = tipo === 'editar';
  const [form, setForm] = useState({
    codigo: editando ? cuenta.codigo : (padre ? padre.codigo : ''),
    nombre: editando ? cuenta.nombre : '',
    padre_codigo: padre?.codigo || cuenta?.padre_codigo || '',
    naturaleza: editando ? cuenta.naturaleza : (padre?.naturaleza || 'D'),
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editando) {
        await editarCuenta(cuenta.codigo, { nombre: form.nombre });
      } else {
        await crearCuenta({
          codigo:       form.codigo,
          nombre:       form.nombre,
          padre_codigo: form.padre_codigo,
          naturaleza:   form.naturaleza,
        });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.modalBg} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <h3 style={S.modalTitle}>
          {editando ? `Editar ${cuenta.codigo}` : `Crear auxiliar bajo ${padre?.codigo}`}
        </h3>
        <form onSubmit={submit}>
          {error && <div style={S.error}>{error}</div>}

          {!editando && (
            <>
              <label style={S.label}>Código (7-10 dígitos, debe empezar con {form.padre_codigo})</label>
              <input
                style={S.input}
                value={form.codigo}
                onChange={e => setForm(f => ({ ...f, codigo: e.target.value }))}
                placeholder={`${form.padre_codigo}01`}
                required
              />
            </>
          )}

          <label style={S.label}>Nombre</label>
          <input
            style={S.input}
            value={form.nombre}
            onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
            required
          />

          {!editando && (
            <>
              <label style={S.label}>Naturaleza</label>
              <select
                style={S.input}
                value={form.naturaleza}
                onChange={e => setForm(f => ({ ...f, naturaleza: e.target.value }))}
              >
                <option value="D">Débito</option>
                <option value="C">Crédito</option>
              </select>
            </>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button type="button" onClick={onClose} style={S.btnSec} disabled={saving}>Cancelar</button>
            <button type="submit" style={S.btnPri} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const S = {
  container: { padding: 24, maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 18 },
  title:  { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  sub:    { color: '#64748b', margin: '4px 0 0', fontSize: 13 },
  toolbar:  { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' },
  search:   { flex: 1, minWidth: 240, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 },
  checkbox: { display: 'flex', alignItems: 'center', gap: 6, color: '#475569', fontSize: 13 },
  count:    { color: '#64748b', fontSize: 13, marginLeft: 'auto' },
  error:    { background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: 8, overflow: 'hidden' },
  th: { background: '#f1f5f9', padding: '10px 12px', textAlign: 'left', fontSize: 12, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid #e2e8f0' },
  td: { padding: '4px 12px', fontSize: 14, color: '#1e293b', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  tdAcciones: { padding: '6px 12px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' },
  btnExpand: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11, color: '#64748b', marginRight: 4, padding: 0, width: 14 },
  btnAdd:   { background: '#10b981', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 },
  btnEdit:  { background: '#3b82f6', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 },
  btnDel:   { background: '#ef4444', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 },
  btnReact: { background: '#64748b', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 },
  iconBtn:  { background: '#1e293b', border: '1px solid #334155', borderRadius: 6, width: 26, height: 26, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 },
  badgeStd: { background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 },
  badgeUser:{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 },
  modalBg: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal:   { background: '#fff', borderRadius: 10, padding: 28, width: 480, maxWidth: '90vw', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' },
  modalTitle: { margin: '0 0 18px', fontSize: 18, color: '#0f172a' },
  label:   { display: 'block', fontSize: 13, color: '#475569', marginBottom: 4, marginTop: 12 },
  input:   { width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  btnSec:  { flex: 1, padding: '10px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  btnPri:  { flex: 1, padding: '10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
};
